/**
 * v1-API-Adapter — typed wrappers around the legacy /v1/admin endpoints.
 *
 * CRM 2.0 routes new functionality (auth, MFA, locale) through `apiV2`
 * (see src/lib/api.ts). All resource data (cares, users, requests, messages,
 * dashboard counters) still lives behind the v1 admin endpoints — this module
 * exposes typed accessors so pages don't have to think about the raw shapes.
 *
 * Conventions:
 *  - Endpoint helpers return either a raw entity, a `LaravelPagination<T>`,
 *    or a plain array, depending on the v1 response.
 *  - Mappers convert v1 entities to the UI types in `src/types`. Where v1
 *    has no equivalent of a UI field (urgency, budget, stage on Anfrage),
 *    we apply a reasonable default — pages can override.
 */

import { apiV1, apiV2 } from './api';
import i18n from '../i18n';
import { formatDate } from './dateFormat';
import { availableFromFromRow } from './bkAvailability';
import type {
  Anfrage,
  Caregiver,
  Client,
  EmailMessage,
  Pflegegrad,
  Stage,
  Urgency,
} from '../types';

/* ─── Laravel pagination ─────────────────────────────────────────────── */

export interface LaravelPagination<T> {
  current_page: number;
  data: T[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

interface PaginatedEnvelope<T> {
  type: 'success' | 'error';
  pagination: LaravelPagination<T>;
}

/* ─── Common list params (page + filters + ordering) ────────────────── */

export interface ListParams {
  page?: number;
  perPage?: number;
  /**
   * Filter values. Arrays are serialized by axios as `filters[key][]=v` which
   * Laravel parses back into a PHP array — required for controller filters that
   * use `whereIn` (e.g. `konto`).
   */
  filters?: Record<string, string | number | boolean | Array<string | number>>;
  /**
   * `profile: true` sorts by a column on the joined `profiles` table
   * (vorname/nachname/ort/pflegegrad …); otherwise the field is a `users`
   * column (id/email/created_at/konto …). Mirrors getUsersByRole, which
   * checks `orderBy[profile] === 'true'`.
   */
  orderBy?: { field: string; profile?: boolean };
  /** 1 = asc, -1 = desc, 0 = none (uses backend default) */
  order?: 1 | -1 | 0;
}

/**
 * Flatten ListParams to bracket-notation query params Laravel understands.
 * Many controllers iterate `$request->filters` with `foreach`, which throws
 * when `filters` is null — so we always include at least `filters[_]=1`
 * to guarantee an array key.
 */
function buildParams(p: ListParams = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.page !== undefined) out.page = p.page;
  if (p.perPage !== undefined) out.perPage = p.perPage;

  const filters = p.filters ?? {};
  const keys = Object.keys(filters);
  if (keys.length === 0) {
    out['filters[_]'] = '1';
  } else {
    for (const k of keys) {
      // Array values keep the un-bracketed key — axios appends `[]` so Laravel
      // receives `filters[konto][]=4` → filters['konto'] = ['4'] (for whereIn).
      out[`filters[${k}]`] = filters[k];
    }
  }

  if (p.orderBy) {
    out['orderBy[field]'] = p.orderBy.field;
    // Controller checks `=== 'true'` (string), so send the literal.
    if (p.orderBy.profile) out['orderBy[profile]'] = 'true';
  }
  if (p.order !== undefined) {
    out.order = p.order;
  }
  return out;
}

/* ─── Raw v1 entity shapes ──────────────────────────────────────────── */
// Loose typing: Eloquent serialization yields extra dynamic columns we don't
// model exhaustively. Add fields as pages need them.

export interface V1Profile {
  id?: number;
  user_id?: number;
  anrede?: string | null;
  titel?: string | null;
  vorname?: string | null;
  nachname?: string | null;
  geburtsdatum?: string | null;
  pflegegrad?: number | null;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  ort?: string | null;
  land?: string | null;
  telefon?: string | null;
  [key: string]: unknown;
}

export interface V1User {
  id: number;
  konto: number;       // account state: 1=incomplete, 2=new, 3=translation, 4=active
  status: number;
  user_role: number;   // 1 = BK (caregiver), 2 = PB (care recipient)
  mobile?: string | null;
  email?: string | null;
  startdatum?: string | null;
  /** Nur BK-Liste (GET /v2/admin/bk): Ende der letzten (auch vergangenen) Abwesenheit, YYYY-MM-DD. */
  last_absence_end?: string | null;
  /** Nur BK-Liste: mindestens eine Abwesenheit ohne Enddatum. */
  has_open_absence?: boolean;
  fullname?: string;
  vorname?: string;
  nachname?: string;
  price?: number | null;
  pflegegrad?: number | null;
  priority?: 0 | 1 | 2;
  created_at: string;
  updated_at: string;
  /** Some endpoints (getUsersByRole) attach a `profiles` array; others (getUsers) inline a single profile. */
  profile?: V1Profile | null;
  profiles?: V1Profile[];
  /** Contract fields surfaced by the PB-phase summary endpoints (documented; index signature still covers them at runtime). */
  assigned_admin_name?: string | null;
  /** Admin id assigned to this user (PB-context). */
  assigned_to?: number | null;
  /** Leadquelle: Quellsystem (z.B. "website", "telefon", "empfehlung"). */
  lead_quelle?: string | null;
  /** Leadquelle-Info: Zusatzinfo zum Quellsystem (z.B. Form-ID, User-ID, URL). */
  lead_quelle_info?: string | null;
  bk_name?: string | null;
  bk_user_id?: number | null;
  /** PB-Liste: aktuell laufende Abwesenheit (oder null), von getUsersByRole angehängt. */
  current_absence?: V1Absence | null;
  /** WP4: aktuelle Preiszeile — wird von getFullUser() angehängt. */
  priceCurrent?: Record<string, unknown> | null;
  /** KI-Übersetzung TP1 — mehrsprachige Inhalte (primäres Profil + User). */
  content_translations?: import('./contentTranslations').ContentTranslations | null;
  /** WP4: alle Preiszeilen — alternative zu priceCurrent. */
  prices?: Array<Record<string, unknown>>;
  /** Batch-13 — Wiedervorlage: Datum (YYYY-MM-DD) + optionale Notiz. */
  wiedervorlage_am?: string | null;
  wiedervorlage_notiz?: string | null;
  /**
   * Hauptkontakt-Auflösung (additiv, nur bei Zeilen aus PbListController):
   * der echte Pflegeempfänger (is_pflegeempfaenger) des Familienkontos
   * dieser Zeile — kann von `id`/`profile` abweichen, wenn diese Zeile im
   * Familienkonto selbst eine Kontaktperson mit eigenem Account ist (z. B.
   * "Max junior"). null = kein Familienkonto oder kein PE markiert.
   */
  pe_user_id?: number | null;
  pe_profile_id?: number | null;
  pe_name?: string | null;
  /**
   * Rohwert wie `profiles.pflegegrad`/`profiles.geburtsdatum`, aber vom
   * Pflegeempfänger (pe_user_id) statt vom Profil dieser Zeile — additiv,
   * damit Kanban-Kachel/Tabelle unter /anfragen den PE-Pflegegrad ohne
   * Nachladen anzeigen können (s. PbListController::enrichRows).
   */
  pe_pflegegrad?: number | null;
  pe_geburtsdatum?: string | null;
  /**
   * Ort des Pflegeempfängers (pe_user_id), analog pe_pflegegrad/
   * pe_geburtsdatum — PbListController::enrichRows liefert ihn additiv mit,
   * damit v1UserToClient() bei PE-Override auch den Ort vom Pflegeempfänger
   * statt von der Anfrage-/Kontaktpersonen-Zeile nimmt.
   */
  pe_ort?: string | null;
  /** Ansprechpartner des Familienkontos (nicht der Pflegeempfänger, s. pe_name). */
  contact_name?: string | null;
  contact_email?: string | null;
  [key: string]: unknown;
}

/** Pick the most relevant Profile from whatever shape the endpoint returned. */
export function primaryProfile(u: V1User): V1Profile | null {
  if (u.profile) return u.profile;
  if (u.profiles && u.profiles.length > 0) return u.profiles[0];
  return null;
}

export interface V1Care {
  id: number;
  status: number;       // 0=new, 2=confirmed, 3=running, ...
  startdatum: string;
  enddatum: string | null;
  bk_user_id: number;
  pb_user_id: number;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface V1CareRequest {
  id: number;
  user_id?: number;
  pb_user_id?: number;
  status?: number;
  datum?: string | null;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface V1Message {
  id: number;
  user_id?: number;
  von?: string | number;
  betreff?: string | null;
  text?: string | null;
  status?: number;
  confirmation?: number;
  confirmed_at?: string | null;
  declined_at?: string | null;
  assigned_to?: number | null;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

export interface V1Chat {
  user_id: number;
  total: number;
}

export interface V1Contact {
  id: number;
  user_id?: number;
  kategorie?: number | string | Array<number | string> | null;
  account?: number | string | null;
  vorname?: string | null;
  nachname?: string | null;
  fullname?: string | null;
  telefon?: string | null;
  mobile?: string | null;
  email?: string | null;
  [key: string]: unknown;
}

export interface V1ContactType {
  id: number;
  name: string;
  [key: string]: unknown;
}

/** Admin (CRM operator) — different table than User (BK/PB). */
export interface V1Admin {
  id: number;
  konto: number;       // account state
  role: number;        // role id (1=super-admin, etc.)
  vorname?: string | null;
  nachname?: string | null;
  email: string;
  mobile?: string | null;
  profile_image?: string | null;
  last_action_at?: string | null;
  created_at?: string;
  updated_at?: string;
  groups?: Array<{ id: number; name?: string }>;
  [key: string]: unknown;
}

/** Payload shape expected by `PUT /v1/admin/admin/{id}` (validator-required fields). */
export interface V1AdminUpdatePayload {
  konto: number;
  role: number;
  email: string;
  vorname: string;
  nachname: string;
  mobile?: string | null;
  /** Optional on update — only sent when the operator sets a new password. */
  password?: string;
}

/** Admin role (admin_roles table) — id + display name, mirrors RBAC role names. */
export interface V1AdminRole {
  id: number;
  name: string;
  [key: string]: unknown;
}

export interface V1Badges {
  dashboard: number;
  tasks: number;
  cares: number;
  communication: number;
  translation: number;
  new_users: number;
  urgent_users: number;
  confirmation_messages: number;
  leistungsnachweise: number;
}

/* ─── Endpoint accessors ────────────────────────────────────────────── */

/** Dashboard counters + lists (used by DashboardPage). */
export const v1Dashboard = {
  /** GET /v1/admin/badges → counter object for the sidebar + dashboard tiles. */
  badges: async (): Promise<V1Badges> => {
    const res = await apiV1.get<{ type: string; badges: V1Badges }>('/admin/badges');
    return res.data.badges;
  },

  /** GET /v1/admin/new-users → paginated User (konto=2, optionally including konto=1). */
  newUsers: async (params?: ListParams) => {
    const filters = { showUnfinishedUsers: 'false', ...(params?.filters ?? {}) };
    const res = await apiV1.get<PaginatedEnvelope<V1User>>(
      '/admin/new-users',
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/new-cares → paginated Care (status=0). */
  newCares: async (params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<V1Care>>(
      '/admin/new-cares',
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/current-cares → running cares spanning today. */
  currentCares: async (params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<V1Care>>(
      '/admin/current-cares',
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/upcoming-cares → confirmed cares starting in the future. */
  upcomingCares: async (params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<V1Care>>(
      '/admin/upcoming-cares',
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /**
   * GET /v1/admin/urgent-cares → PBs needing a caregiver within 8 weeks.
   * Rows: users.* + `pflegegrad` + `priority` (1 = noch nie eine bestätigte
   * Betreuung, 2 = Anschlussbetreuung geplant, 0 = sonst) + Name
   * (`fullname`/`vorname`/`nachname`). Sortierung über `orderBy.field`
   * (`konto`/`id`/`startdatum`/`pflegegrad`) + `order`; Default startdatum asc.
   */
  urgentCares: async (params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<V1User>>(
      '/admin/urgent-cares',
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /**
   * GET /v1/admin/tasks → globale Aufgabenliste (letzte 3 Monate).
   * Filter als Strings "true"/"false": `showFinishedTasks` (Default hier
   * 'false' = erledigte ausblenden), `showMyTasks` ('true' = nur Aufgaben,
   * bei denen der Admin Ersteller/Bearbeiter ist bzw. seine Gruppe
   * Bearbeiter-Gruppe ist). Admin-Rollen 3–6 sehen serverseitig immer nur
   * die eigenen Aufgaben.
   */
  tasks: async (params?: ListParams) => {
    const filters = { showFinishedTasks: 'false', ...(params?.filters ?? {}) };
    const res = await apiV1.get<PaginatedEnvelope<V1DashboardTask>>(
      '/admin/tasks',
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/confirmation-messages → admin-confirmable messages. */
  confirmationMessages: async (params?: ListParams) => {
    const filters = { showConfirmedMessages: 'false', ...(params?.filters ?? {}) };
    const res = await apiV1.get<PaginatedEnvelope<V1Message>>(
      '/admin/confirmation-messages',
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/new-chats → unread customer chats grouped by user. */
  newChats: async (): Promise<V1Chat[]> => {
    const res = await apiV1.get<{ type: string; chats: V1Chat[] }>('/admin/new-chats');
    return res.data.chats;
  },

  /**
   * GET /v1/admin/leistungsnachweise — paginated proofs-of-service.
   * `showMonth` MUST be an ISO date inside the month to show (controller
   * accesses `filters[showMonth]` unconditionally).
   */
  leistungsnachweise: async (showMonth: string, params?: ListParams) => {
    const filters = {
      showMonth,
      showAllLeistungsnachweise: 'false',
      ...(params?.filters ?? {}),
    };
    const res = await apiV1.get<PaginatedEnvelope<Record<string, unknown>>>(
      '/admin/leistungsnachweise',
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },
};

/** Care-Requests (BK ↔ PB matching requests) used by AnfragenPage. */
export const v1Requests = {
  list: async (params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<V1CareRequest>>(
      '/admin/requests',
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },
};

/** Payload for creating a new Care deployment. */
export interface V1CareCreatePayload {
  pb_user_id: number;
  bk_user_id: number;
  startdatum: string;
  enddatum?: string | null;
  /** 0=Planung, 1=Storniert, 2=Bestätigt, 3=Vorbereitung */
  status?: number;
  notify?: boolean;
  /** true = update the PB's startdatum from this care */
  changeStartdate?: boolean;
  /**
   * How the care was created. The `origin` column in the DB is NOT NULL
   * with no server-side default — callers must always provide this.
   * CRM 1.0 hardcodes 'manuell' for admin-created cares; we do the same.
   */
  origin?: string;
}

/** Payload for updating an existing Care deployment. */
export interface V1CareUpdatePayload {
  bk_user_id?: number;
  pb_user_id?: number;
  startdatum?: string;
  enddatum?: string | null;
  /** 0=Planung, 1=Storniert, 2=Bestätigt, 3=Vorbereitung, 4=Abgeschlossen */
  status?: number;
  notify?: boolean;
  changeStartdate?: boolean;
  /** Batch-12: gespeichertes Flag — Pflegedienst soll benachrichtigt werden. */
  pflegedienst_benachrichtigen?: boolean;
  origin?: string;
  /** Überlappung > 1 Tag mit einer anderen Betreuung bewusst bestätigt (nur v2). */
  confirm_overlap?: boolean;
  /**
   * Optimistisches Locking (nur v2Cares.update): der beim Laden erhaltene
   * `updated_at`-Stand. Weicht er ab → 409 E_STALE_WRITE.
   */
  updated_at?: string | null;
}

/** Cares (deployments) — no listing endpoint, only single fetch + dashboard variants. */
export const v1Cares = {
  /** GET /v1/admin/care/{id} → single Care detail. */
  get: async (id: number): Promise<V1Care> => {
    const res = await apiV1.get<{ type: string; care: V1Care }>(`/admin/care/${id}`);
    return res.data.care;
  },

  /**
   * POST /v1/admin/care — creates a new Care deployment.
   *
   * Required: pb_user_id, bk_user_id, startdatum.
   * enddatum is required when status ∈ {0, 2}.
   *
   * The backend may return { type:'error', message: '…' } as HTTP 200 for
   * validation failures (e.g. overlapping cares). We throw in that case so
   * callers always get an Error.
   */
  create: async (payload: V1CareCreatePayload): Promise<V1Care> => {
    // `origin` is a NOT NULL column with no DB default — always include it.
    // CRM 1.0 uses 'manuell' for admin-created cares; mirror that behaviour.
    const body: V1CareCreatePayload = { origin: 'manuell', ...payload };
    try {
      const res = await apiV1.post<{ type: string; care?: V1Care; message?: string }>(
        '/admin/care',
        body,
      );
      if (res.data.type === 'error' || !res.data.care) {
        throw new Error(res.data.message ?? 'Betreuung konnte nicht angelegt werden.');
      }
      return res.data.care;
    } catch (err) {
      // Surface 422 API error messages (e.g. E_CARE_OVERLAP_PAUSE / E_CARE_OVERLAP_CARE).
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      if (msg) throw new Error(msg);
      throw err;
    }
  },

  /**
   * PUT /v1/admin/care/{id} — updates an existing Care deployment.
   *
   * enddatum is required when status ∈ {0, 2}.
   * The backend controller is `updateCare` on AppController.
   *
   * The backend may return { type:'error', message: '…' } as HTTP 200 for
   * validation failures. We throw in that case so callers always get an Error.
   */
  update: async (id: number, payload: V1CareUpdatePayload): Promise<V1Care> => {
    const res = await apiV1.put<{ type: string; care?: V1Care; message?: string }>(
      `/admin/care/${id}`,
      payload,
    );
    if (res.data.type === 'error' || !res.data.care) {
      throw new Error(res.data.message ?? 'Betreuung konnte nicht aktualisiert werden.');
    }
    return res.data.care;
  },
};

/**
 * Matching endpoint — returns a ranked list of BK candidates for a given PB.
 * GET /v1/admin/user/{pbId}/matching?compare_user={bkId} (or without compare_user
 * to get all ranked BKs).
 *
 * Active BKs have konto===4; status!==2 means not cancelled.
 */
export const v1Matching = {
  /** GET /v1/admin/user/{userId}/matching — BK matching list for a PB. */
  listForUser: async (userId: number): Promise<V1User[]> => {
    const res = await apiV1.get<{ type: string; users?: V1User[]; matching?: V1User[] }>(
      `/admin/user/${userId}/matching`,
    );
    // Backend may return users or matching key
    return (res.data.users ?? res.data.matching ?? []) as V1User[];
  },
};

/** Users — flat list (for Communication) + role-filtered (for BK/PB tabs). */
export const v1Users = {
  /** GET /v1/admin/users → all users (slim payload). */
  list: async (): Promise<V1User[]> => {
    const res = await apiV1.get<{ type: string; users: V1User[] }>('/admin/users');
    return res.data.users;
  },

  /**
   * GET /v1/admin/users/{role_id} — paginated by role. The legacy controller
   * keys on `User.user_role` (smallint), so the URL segment must be the
   * numeric ID (1=BK, 2=PB), not the string. The public API still takes
   * 'BK'/'PB' for readability; we translate here.
   * The controller `foreach`-iterates `filters`, so an array (possibly
   * empty via buildParams' placeholder) is required.
   */
  byRole: async (role: 'BK' | 'PB', params?: ListParams) => {
    const roleId = role === 'BK' ? 1 : 2;
    const res = await apiV1.get<PaginatedEnvelope<V1User>>(
      `/admin/users/${roleId}`,
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /**
   * GET /v1/admin/pb/phase-counts → aggregate PB count per phase (1–5)
   * plus a grand total, computed server-side across ALL matching PB (not just
   * the loaded page). Honors the same filters as the list EXCEPT `phase`
   * (the caller should pass its non-phase filters so the chip counts reflect
   * the active search/status/pflegegrad/assigned/bk filters).
   */
  pbPhaseCounts: async (
    filters?: ListParams['filters'],
  ): Promise<{ counts: Record<string, number>; total: number }> => {
    const res = await apiV1.get<{ type: string; counts: Record<string, number>; total: number }>(
      '/admin/pb/phase-counts',
      { params: buildParams({ filters }) },
    );
    return { counts: res.data.counts, total: res.data.total };
  },

  /** GET /v1/admin/user/{id} → single User (profile inlined). */
  get: async (id: number): Promise<V1User> => {
    const res = await apiV1.get<{ type: string; user: V1User }>(`/admin/user/${id}`);
    return res.data.user;
  },

  /**
   * PUT /v1/admin/user/{id} — partial update of user-level columns
   * (status, konto, email, mobile, address, bank, lifecycle, badge, …).
   * The legacy controller does `$user->update($request->all())`, so only
   * fillable columns get applied; extra keys are ignored harmlessly.
   * Throws on IBAN validation failure.
   */
  update: async (id: number, payload: Record<string, unknown>): Promise<V1User> => {
    const res = await apiV1.put<{ type: string; user: V1User; message?: string }>(`/admin/user/${id}`, payload);
    // Der Legacy-Controller meldet eine ungültige IBAN mit HTTP 200 +
    // type:'error' — ohne diese Prüfung lief der Save „erfolgreich" weiter,
    // obwohl KEIN User-Feld gespeichert wurde.
    if (res.data.type === 'error' || !res.data.user) {
      throw new Error(res.data.message || 'Speichern der Personendaten fehlgeschlagen.');
    }
    return res.data.user;
  },

  /** POST /v1/admin/user/{id}/price → adds a new PricePB record (active flag handled server-side). */
  createPrice: async (id: number, payload: Record<string, unknown>): Promise<unknown> => {
    const res = await apiV1.post(`/admin/user/${id}/price`, payload);
    return res.data;
  },

  /**
   * POST /v1/admin/user/{id}/bonus → legt einen Ausgleichsbetrag (PriceBonus) an.
   * Backend (AdminController::createBonus) validiert:
   *   gueltig_fuer: required|date, bonus: required|numeric.
   */
  createBonus: async (id: number, payload: Record<string, unknown>): Promise<unknown> => {
    const res = await apiV1.post(`/admin/user/${id}/bonus`, payload);
    return res.data;
  },

  /**
   * GET /v1/admin/user/{id}/cares → paginated Care list for this user.
   * Each row carries injected `pb_name`, `bk_name`, `pb_rating`,
   * `bk_rating`, `matching` (score) — see UserController::getUserCares.
   * `filters[showOldCares]` is required by the controller; default to "false".
   */
  cares: async (id: number, params?: ListParams) => {
    const filters = { showOldCares: 'false', ...(params?.filters ?? {}) };
    const res = await apiV1.get<PaginatedEnvelope<V1Care>>(
      `/admin/user/${id}/cares`,
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/user/{id}/travels → paginated travel/transfer rows (BK-only). */
  travels: async (id: number, params?: ListParams) => {
    const filters = { showOldTravels: 'false', ...(params?.filters ?? {}) };
    const res = await apiV1.get<PaginatedEnvelope<Record<string, unknown>>>(
      `/admin/user/${id}/travels`,
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/user/{id}/messages → paginated Message list for this user. */
  messages: async (id: number, params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<V1Message>>(
      `/admin/user/${id}/messages`,
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /**
   * GET /v1/admin/user/{id}/tasks → paginated Task list with `activities`
   * inlined per row. Filters mirror the legacy controller's expectations
   * (showFinishedTasks / showMyTasks as the string "true"/"false").
   */
  tasks: async (id: number, params?: ListParams) => {
    const filters = { showFinishedTasks: 'false', ...(params?.filters ?? {}) };
    const res = await apiV1.get<PaginatedEnvelope<Record<string, unknown>>>(
      `/admin/user/${id}/tasks`,
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/user/{id}/ratings/recieved → paginated received ratings. */
  ratingsReceived: async (id: number, params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<Record<string, unknown>>>(
      `/admin/user/${id}/ratings/recieved`,
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/user/{id}/ratings/created → paginated written ratings. */
  ratingsCreated: async (id: number, params?: ListParams) => {
    const res = await apiV1.get<PaginatedEnvelope<Record<string, unknown>>>(
      `/admin/user/${id}/ratings/created`,
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /**
   * GET /v2/admin/users/{id}/ratings/{received|created} — Bewertungen mit
   * aufgeloesten Namen: `von_name` (Profilname ODER Freitext), `von_linked`
   * (mit Profil verknuepft), `fuer_name` (bewertete Person).
   */
  ratingsList: async (id: number, kind: 'received' | 'created'): Promise<V2RatingRow[]> => {
    const res = await apiV2.get<{ data: V2RatingRow[] }>(`/admin/users/${id}/ratings/${kind}`);
    return res.data.data ?? [];
  },

  /**
   * POST /v2/admin/users/{id}/ratings — Bewertung fuer PB oder BK anlegen.
   * Bewertende Person: `von_user_id` (Profil) ODER `von_name` (Freitext).
   * Validierungsfehler kommen als HTTP 422 und werden zu einem Fehler.
   */
  createRating: async (id: number, payload: V2RatingCreate): Promise<void> => {
    try {
      await apiV2.post(`/admin/users/${id}/ratings`, payload);
    } catch (e) {
      const err = e as { response?: { data?: { errors?: Array<{ code?: string; details?: Record<string, string[]> }> } } };
      if (err.response?.data?.errors?.[0]?.code === 'E_RATING_RATER_ROLE') {
        throw new Error('Die bewertende Person hat nicht die passende Rolle.');
      }
      const details = err.response?.data?.errors?.[0]?.details;
      const first = details ? Object.values(details)[0]?.[0] : undefined;
      throw new Error(first || (e instanceof Error ? e.message : 'Bewertung konnte nicht gespeichert werden.'));
    }
  },

  /**
   * POST /v2/admin/users/{id}/claim — assign the currently authenticated admin
   * as the responsible admin for this PB user (Anfrage übernehmen).
   * Returns { assigned_to: number, assigned_to_name: string }.
   *
   * NOTE: This is a v2 endpoint; grouped here because it is user-related.
   * v1Admins.list() returns all admins; konto===4 = active (page filters client-side).
   */
  claimAnfrage: async (userId: number): Promise<{ assigned_to: number; assigned_to_name: string; phase?: number }> => {
    const res = await apiV2.post<{ data: { id: number; assigned_to: number; assigned_to_name: string; phase?: number } }>(
      `/admin/users/${userId}/claim`,
    );
    return {
      assigned_to: res.data.data.assigned_to,
      assigned_to_name: res.data.data.assigned_to_name,
      phase: res.data.data.phase,
    };
  },

  /**
   * PATCH /v2/admin/users/{id}/assigned-to — freie Zuweisung an einen
   * beliebigen Admin (oder `null` zum Entziehen).
   *
   * Befund 23.09.2026: ersetzt den bisherigen `v1Users.update(id,
   * {assigned_to})`-Aufruf aus PbSidebar.tsx. Der v1-Endpunkt konnte nicht
   * erweitert werden (API 1.0 wird nicht angefasst, s. CLAUDE.md) — dieser
   * v2-Endpunkt spiegelt serverseitig zusätzlich auf
   * `family_accounts.assigned_to` + Geschwister-Anfragen des Kontos
   * (AssignedToSyncService), damit eine Zuweisung nicht mehr je Blickwinkel
   * (Profil-Sidebar vs. /anfragen-Liste vs. Konto) auseinanderläuft.
   */
  setAssignedTo: async (userId: number, adminId: number | null): Promise<{ assigned_to: number | null; assigned_to_name: string | null }> => {
    const res = await apiV2.patch<{ data: { id: number; assigned_to: number | null; assigned_to_name: string | null } }>(
      `/admin/users/${userId}/assigned-to`,
      { assigned_to: adminId },
    );
    return {
      assigned_to: res.data.data.assigned_to,
      assigned_to_name: res.data.data.assigned_to_name,
    };
  },
};

/* ─── v2 User mutations (feature/pb-phase-process) ─────────────────────── */

export interface PhaseUpdateResult {
  id: number;
  phase: number;
  phase_label: string | null;
  phase_color?: string | null;
}

/**
 * v2 user mutations — currently just the phase endpoint.
 * Uses apiV2 (Bearer token from localStorage, same as auth).
 */
export const v2Users = {
  /**
   * PATCH /v2/admin/users/{id}/phase
   *
   * action="advance" → phase+1 (capped at 5)
   * phase=N          → set directly (1–5)
   */
  setPhase: async (
    id: number,
    payload: { action: 'advance' } | { phase: number },
  ): Promise<PhaseUpdateResult> => {
    const res = await apiV2.patch<{ data: PhaseUpdateResult }>(
      `/admin/users/${id}/phase`,
      payload,
    );
    return res.data.data;
  },

  /**
   * PATCH /v2/admin/users/{id}/wiedervorlage — Batch-13.
   *
   * wiedervorlage_am=null löscht die Wiedervorlage (Notiz wird serverseitig
   * mitgelöscht — Datum ist das führende Feld).
   *
   * Befund 23.09.: `wiedervorlage_um` (Uhrzeit, "H:i", Default "00:00" =
   * ganztägig) — additiv. `users.wiedervorlage_am`/`_notiz` bleiben
   * Datum-only (v1-Spiegel, unverändert); die Antwort trägt die Uhrzeit der
   * gerade geschriebenen `wiedervorlagen`-Zeile trotzdem mit, damit das
   * Formular nach dem Speichern nicht auf "00:00" zurückspringt.
   */
  setWiedervorlage: async (
    id: number,
    payload: { wiedervorlage_am: string | null; wiedervorlage_notiz?: string | null; wiedervorlage_um?: string | null },
  ): Promise<{ id: number; wiedervorlage_am: string | null; wiedervorlage_notiz: string | null; wiedervorlage_um?: string | null }> => {
    const res = await apiV2.patch<{ data: { id: number; wiedervorlage_am: string | null; wiedervorlage_notiz: string | null; wiedervorlage_um?: string | null } }>(
      `/admin/users/${id}/wiedervorlage`,
      payload,
    );
    return res.data.data;
  },
};

/* ─── v2 Wiedervorlage (Batch-13) ────────────────────────────────────────── */

export type WiedervorlageScope = 'due' | 'today' | 'overdue';

/**
 * GET /v2/admin/pb/wiedervorlage-faellig (bzw. /bk/… für BK-Anfragen) — global
 * fällige/überfällige Anfragen (nicht nur die aktuell geladene Seite der Anfragen-Liste, die aus
 * dem v1-Endpunkt kommt und "wiedervorlage" als Filter nicht kennt).
 * Antwortform mirrort die v1-PB-Liste (rohe User-Objekte inkl. `profiles`),
 * daher direkt mit `v1UserToAnfrage` weiterverarbeitbar.
 */
export const v2Wiedervorlage = {
  faellig: async (
    scope: WiedervorlageScope = 'due',
    params?: { page?: number; per_page?: number },
    role: 'pb' | 'bk' = 'pb',
  ): Promise<{
    data: V1User[]; total: number; current_page: number; last_page: number;
  }> => {
    const res = await apiV2.get<{ data: V1User[]; meta: { total: number; current_page: number; last_page: number } }>(
      `/admin/${role}/wiedervorlage-faellig`,
      { params: { scope, ...params } },
    );
    return { data: res.data.data, ...res.data.meta };
  },
};

/* ─── v2 Phase management (Phasen-Verwaltung) ───────────────────────────── */

export interface PbPhase {
  id: number;
  sort: number;
  title: string;
  color: string;
  description: string | null;
  is_active: boolean;
}

export interface PhaseCreatePayload {
  title: string;
  color: string;
  description?: string | null;
  is_active?: boolean;
  sort?: number;
}

export interface PhaseUpdatePayload {
  title?: string;
  color?: string;
  description?: string | null;
  is_active?: boolean;
  sort?: number;
}

/**
 * v2 phase management — CRUD + reorder for pb_phases.
 * Gated by menu.einstellungen permission server-side.
 */
export const v2Phases = {
  /** GET /v2/admin/pb/phases → Board-Spalten, lesend (users.read statt menu.einstellungen). */
  board: async (): Promise<PbPhase[]> => {
    const res = await apiV2.get<{ data: PbPhase[]; errors: null }>('/admin/pb/phases');
    return res.data.data;
  },

  list: async (): Promise<PbPhase[]> => {
    const res = await apiV2.get<{ data: PbPhase[]; errors: null }>('/admin/phases');
    return res.data.data;
  },

  create: async (payload: PhaseCreatePayload): Promise<PbPhase> => {
    const res = await apiV2.post<{ data: PbPhase; errors: null }>('/admin/phases', payload);
    return res.data.data;
  },

  update: async (id: number, payload: PhaseUpdatePayload): Promise<PbPhase> => {
    const res = await apiV2.put<{ data: PbPhase; errors: null }>(`/admin/phases/${id}`, payload);
    return res.data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/phases/${id}`);
  },

  reorder: async (ids: number[]): Promise<void> => {
    await apiV2.put('/admin/phases/reorder', { ids });
  },

  counts: async (): Promise<PhaseCount[]> => {
    const res = await apiV2.get<{ data: PhaseCount[]; errors: null }>('/admin/phases/counts');
    return res.data.data;
  },
};

export interface PhaseCount {
  id: number;
  title: string;
  color: string;
  count: number;
}

/* ─── Batch-13 — VBS-Dashboard: Anrufliste ──────────────────────────────── */

export interface CallListRow {
  id: number;
  name: string;
  mobile: string | null;
  telefon: string | null;
  ort: string | null;
  phase: number;
  phase_label: string | null;
  assigned_to: number | null;
  assigned_to_name: string | null;
  /** ISO8601, letzter OUTBOUND-Anrufversuch (eigen + Familienkonto-Mitglieder) — null = nie angerufen. */
  last_call_at: string | null;
  never_called: boolean;
}

/**
 * GET /v2/admin/dashboard/call-list — Anfragen, die angerufen werden müssen,
 * sortiert nach letztem Anrufversuch (ältester/nie angerufen zuerst).
 * Siehe CallListController-Docblock (sh24-api) für die fachliche Definition.
 */
export const v2Dashboard = {
  callList: async (params?: { page?: number; per_page?: number }): Promise<{
    data: CallListRow[]; total: number; current_page: number; last_page: number;
  }> => {
    const res = await apiV2.get<{ data: CallListRow[]; meta: { total: number; current_page: number; last_page: number } }>(
      '/admin/dashboard/call-list',
      { params },
    );
    return { data: res.data.data, ...res.data.meta };
  },

  /**
   * GET /v2/admin/dashboard/tasks — wie v1 /admin/tasks (gleiche Filter,
   * gleiches Antwortformat), aber nach Fälligkeit sortiert: früheste zuerst,
   * Aufgaben ohne Fälligkeit zuletzt (serverseitig, damit es seitenübergreifend stimmt).
   */
  tasks: async (params?: ListParams) => {
    const filters = { showFinishedTasks: 'false', ...(params?.filters ?? {}) };
    const res = await apiV2.get<PaginatedEnvelope<V1DashboardTask>>(
      '/admin/dashboard/tasks',
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },
};

/* ─── Leadquellen-Katalog (Admin-Einstellungen → Allgemein → Leadquellen) ── */

export interface LeadQuelle {
  id: number;
  name: string;
  /** Freitext-Icon (Emoji oder kurzes Kürzel) — rein visuell. */
  icon: string | null;
  /** Freitext-Kampagne, die zu dieser Quelle gehört (z. B. "Google Ads Q4"). */
  kampagne: string | null;
  sort: number;
  aktiv: boolean;
}

export interface LeadQuellePayload {
  name?: string;
  icon?: string | null;
  kampagne?: string | null;
  aktiv?: boolean;
  sort?: number;
}

/**
 * Leadquellen-Katalog. `list()` darf jeder Admin lesen (das Quelle-Dropdown im
 * „Neue Anfrage"-Drawer braucht es); Schreiben ist serverseitig mit
 * menu.admin_einstellungen gegated.
 */
export const v2LeadQuellen = {
  /** all=true → auch inaktive Quellen (Einstellungen-UI). */
  list: async (all = false): Promise<LeadQuelle[]> => {
    const res = await apiV2.get<{ data: LeadQuelle[]; errors: null }>(
      `/admin/lead-quellen${all ? '?all=1' : ''}`,
    );
    return res.data.data;
  },

  create: async (payload: LeadQuellePayload & { name: string }): Promise<LeadQuelle> => {
    const res = await apiV2.post<{ data: LeadQuelle; errors: null }>('/admin/lead-quellen', payload);
    return res.data.data;
  },

  update: async (id: number, payload: LeadQuellePayload): Promise<LeadQuelle> => {
    const res = await apiV2.put<{ data: LeadQuelle; errors: null }>(`/admin/lead-quellen/${id}`, payload);
    return res.data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/lead-quellen/${id}`);
  },
};

/* ─── Anfrage-Abbruch: Gründe-Katalog + Abbruch-Endpoint ────────────────── */

/** Abbruch-Gründe gelten entweder für PB- oder für BK-Anfragen. */
export type AbbruchScope = 'pb' | 'bk';

export interface AbbruchGrund {
  id: number;
  scope: AbbruchScope;
  sort: number;
  titel: string;
  beschreibung: string | null;
  /** true (z. B. "Sonstiges") → Freitext-Beschreibung ist beim Abbruch Pflicht. */
  requires_beschreibung: boolean;
  is_active: boolean;
}

export interface AbbruchGrundPayload {
  titel?: string;
  beschreibung?: string | null;
  requires_beschreibung?: boolean;
  is_active?: boolean;
  sort?: number;
}

/**
 * Gründe-Katalog (Einstellungen → Abbruch-Gründe).
 * list() liest jeder Admin (Abbruch-Dialog); Schreib-Endpoints sind
 * serverseitig mit menu.einstellungen gegated.
 */
export const v2AbbruchGruende = {
  /** all=true → auch inaktive Gründe (Einstellungen-UI); scope → nur PB- bzw. BK-Gründe. */
  list: async (all = false, scope?: AbbruchScope): Promise<AbbruchGrund[]> => {
    const params: Record<string, string> = {};
    if (all) params.all = '1';
    if (scope) params.scope = scope;
    const res = await apiV2.get<{ data: AbbruchGrund[]; errors: null }>(
      '/admin/anfrage-abbruch-gruende', { params },
    );
    return res.data.data;
  },

  create: async (payload: AbbruchGrundPayload & { titel: string; scope: AbbruchScope }): Promise<AbbruchGrund> => {
    const res = await apiV2.post<{ data: AbbruchGrund; errors: null }>('/admin/anfrage-abbruch-gruende', payload);
    return res.data.data;
  },

  update: async (id: number, payload: AbbruchGrundPayload): Promise<AbbruchGrund> => {
    const res = await apiV2.put<{ data: AbbruchGrund; errors: null }>(`/admin/anfrage-abbruch-gruende/${id}`, payload);
    return res.data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/anfrage-abbruch-gruende/${id}`);
  },
};

export interface AnfrageAbbruchResult {
  id: number;
  /** Neue (negierte) Phase, z. B. -2. */
  phase: number;
  phase_vorher: number | null;
  grund_id: number;
  grund_titel: string;
}

/**
 * POST /v2/admin/users/{id}/anfrage-abbruch — Anfrage mit Grund abbrechen.
 * Setzt users.phase auf den negierten Wert (Phase 2 → -2); die Anfrage
 * verschwindet damit aus allen Pipeline-Listen (excludeCancelled-Filter).
 * Permission: users.write.
 */
export const v2AnfrageAbbruch = {
  abbrechen: async (
    userId: number,
    payload: { grund_id: number; beschreibung?: string },
  ): Promise<AnfrageAbbruchResult> => {
    const res = await apiV2.post<{ data: AnfrageAbbruchResult }>(
      `/admin/users/${userId}/anfrage-abbruch`,
      payload,
    );
    return res.data.data;
  },

  /**
   * Batch-12 — POST /v2/admin/users/{id}/anfrage-wiederherstellen:
   * hebt den Abbruch auf, users.phase springt auf die Phase vor dem Abbruch.
   */
  wiederherstellen: async (userId: number): Promise<{ id: number; phase: number }> => {
    const res = await apiV2.post<{ data: { id: number; phase: number } }>(
      `/admin/users/${userId}/anfrage-wiederherstellen`,
    );
    return res.data.data;
  },
};

/* ─── Verordnungen (Batch-12 — Verordnungsmanagement) ───────────────────── */

export interface Verordnung {
  id: number;
  user_id: number;
  titel: string;
  beschreibung: string | null;
  /** Termin (YYYY-MM-DD). */
  termin: string;
  /** Erinnerung: X Tage vor dem Termin (null = keine). */
  erinnerung_tage: number | null;
  created_by: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface VerordnungPayload {
  titel: string;
  beschreibung?: string | null;
  termin: string;
  erinnerung_tage?: number | null;
}

export const v2Verordnungen = {
  listForUser: async (userId: number): Promise<Verordnung[]> => {
    const res = await apiV2.get<{ data: Verordnung[] }>(`/admin/pb/${userId}/verordnungen`);
    return res.data.data;
  },
  create: async (userId: number, payload: VerordnungPayload): Promise<Verordnung> => {
    const res = await apiV2.post<{ data: Verordnung }>(`/admin/pb/${userId}/verordnungen`, payload);
    return res.data.data;
  },
  update: async (id: number, payload: VerordnungPayload): Promise<Verordnung> => {
    const res = await apiV2.put<{ data: Verordnung }>(`/admin/verordnungen/${id}`, payload);
    return res.data.data;
  },
  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/verordnungen/${id}`);
  },
};

/* ─── Beendete Anfragen ─────────────────────────────────────────────────── */

export interface BeendeteAnfrage {
  id: number;
  user_id: number;
  /** Profil-ID für die Familienkonto-Navigation (/pflege/{account}/{profile}). */
  profile_id: number | null;
  family_account_id: number | null;
  name: string;
  email: string | null;
  stage: { id: number | null; title: string | null; color: string | null };
  grund: string | null;
  beschreibung: string | null;
  admin: string | null;
  created_at: string | null;
}

export interface BeendeteAnfragenMeta {
  page: number;
  per_page: number;
  last_page: number;
  total: number;
}

/**
 * GET /v2/admin/anfrage-abbrueche?scope=pb|bk — Beendete (abgebrochene)
 * Anfragen mit Stage (Phase vor dem Abbruch) und Begründung.
 * Permission je Scope: menu.beendete_anfragen / menu.beendete_anfragen_bk.
 */
export const v2BeendeteAnfragen = {
  list: async (
    scope: 'pb' | 'bk',
    opts: { page?: number; per_page?: number; q?: string } = {},
  ): Promise<{ rows: BeendeteAnfrage[]; meta: BeendeteAnfragenMeta }> => {
    const params = new URLSearchParams({ scope });
    if (opts.page) params.set('page', String(opts.page));
    if (opts.per_page) params.set('per_page', String(opts.per_page));
    if (opts.q) params.set('q', opts.q);
    const res = await apiV2.get<{ data: BeendeteAnfrage[]; meta: BeendeteAnfragenMeta }>(
      `/admin/anfrage-abbrueche?${params.toString()}`,
    );
    return { rows: res.data.data, meta: res.data.meta };
  },
};

/* ─── Phasen-Regelsystem ────────────────────────────────────────────────── */

export interface PhaseRule {
  id: number;
  phase_id: number;
  field: string;
  field_label: string;
  operator: 'not_empty' | 'equals';
  value: string | null;
  aktiv: boolean;
}

export interface PhaseRuleField {
  field: string;
  label: string;
  /**
   * 'derived'  = abgeleitete Bedingung, nicht auf dem users-Datensatz
   *              (Verträge, Bilder, SIS, SGB5, Tagesplan) — nur auswertbar,
   *              nicht direkt ausfüllbar.
   * 'boolean'  = Checkbox auf dem users-Datensatz (Kündigung schriftlich).
   */
  type: 'date' | 'ref' | 'value' | 'derived' | 'boolean';
  /** Zulässige Operatoren für dieses Feld (abgeleitete Felder: nur not_empty). */
  operators?: Array<'not_empty' | 'equals'>;
}

/** Auswertung einer Regel gegen einen konkreten PB (Server-Sicht). */
export interface PhaseRuleStatus {
  id: number;
  phase_id: number;
  field: string;
  operator: 'not_empty' | 'equals';
  value: string | null;
  passes: boolean;
  describe: string;
  /** Nur Bedingung familienkonto_komplett: Feldnamen der fehlenden Pflichtfelder. */
  missing?: string[];
}

export const v2PhaseRules = {
  list: async (): Promise<PhaseRule[]> => {
    const res = await apiV2.get<{ data: PhaseRule[] }>('/admin/phase-rules');
    return res.data.data;
  },

  /**
   * Regel-Status für EINEN PB. Die abgeleiteten Bedingungen liegen nicht auf
   * dem users-Datensatz, das Frontend kann sie darum nicht selbst auswerten.
   */
  status: async (userId: number): Promise<PhaseRuleStatus[]> => {
    const res = await apiV2.get<{ data: PhaseRuleStatus[] }>(`/admin/phase-rules/status/${userId}`);
    return res.data.data;
  },

  fields: async (): Promise<PhaseRuleField[]> => {
    const res = await apiV2.get<{ data: PhaseRuleField[] }>('/admin/phase-rule-fields');
    return res.data.data;
  },

  create: async (input: { phase_id: number; field: string; operator: 'not_empty' | 'equals'; value?: string | null }): Promise<PhaseRule> => {
    const res = await apiV2.post<{ data: PhaseRule }>('/admin/phase-rules', input);
    return res.data.data;
  },

  update: async (id: number, input: Partial<{ field: string; operator: 'not_empty' | 'equals'; value: string | null; aktiv: boolean }>): Promise<PhaseRule> => {
    const res = await apiV2.put<{ data: PhaseRule }>(`/admin/phase-rules/${id}`, input);
    return res.data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/phase-rules/${id}`);
  },
};

/* ─── Phasen-Aktionen (Admin-Einstellungen → PB/BK Stages) ────────────────
 *
 * Auslöser ('enter' | 'exit' | 'claim') führen eine Aktion aus:
 *   'email' → E-Mail aus einer Vorlage (email_templates) an den User
 *   'task'  → Aufgabe (Bearbeiter = zuständige Person, Deadline =
 *             Auslösedatum + optional X Stunden/Tage)
 */
export type PhaseActionTrigger = 'enter' | 'exit' | 'claim';
export type PhaseActionType = 'email' | 'task' | 'docusign' | 'notification' | 'calendar' | 'jump' | 'assign_stage';
export type PhaseActionScope = 'pb' | 'bk';
/** Kanal der Aktion „Benachrichtigung senden". */
export type PhaseActionNotifyChannel = 'email' | 'push';

/** Empfänger-Rolle für die Aktion „Benachrichtigung senden". */
export interface PhaseActionRole {
  id: number;
  name: string;
  /** Wie viele Admins diese Rolle haben — 0 bedeutet: erreicht niemanden. */
  admin_count: number;
}

export interface PhaseActionRow {
  id: number;
  scope: PhaseActionScope;
  phase_id: number;
  trigger: PhaseActionTrigger;
  action_type: PhaseActionType;
  email_template_id: number | null;
  email_template_name: string | null;
  task_titel: string | null;
  task_beschreibung: string | null;
  task_offset_value: number | null;
  task_offset_unit: 'hours' | 'days' | null;
  /* FA-010 — Stage-Aktion „DocuSign-Dokument senden" */
  docusign_template_id: string | null;
  docusign_template_name: string | null;
  docusign_role: string | null;
  /** Empfänger-Variable: 'self' | 'profiltyp:<Kontakttyp>' | 'assigned_admin' */
  docusign_recipient: string | null;
  docusign_internal_role: string | null;
  docusign_internal_recipient: string | null;
  /* Stage-Aktion „Benachrichtigung senden" */
  notify_channel: PhaseActionNotifyChannel | null;
  /** IDs der Empfänger-Rollen */
  notify_roles: number[];
  /** Namen zu notify_roles — kürzer, wenn eine Rolle gelöscht wurde */
  notify_role_names: string[];
  notify_subject: string | null;
  notify_message: string | null;
  /* Stage-Aktion „Kalendereintrag erstellen" */
  calendar_titel: string | null;
  calendar_beschreibung: string | null;
  calendar_offset_value: number | null;
  calendar_offset_unit: 'hours' | 'days' | null;
  calendar_duration_minutes: number | null;
  calendar_category_id: number | null;
  /** Stage-Aktion „Springe in Stage X" — Ziel-Stage (pb_phases.id/bk_phases.id je nach scope). */
  jump_target_phase_id: number | null;
  /**
   * Stage-Aktion „Stage zuweisen" — Abteilung + Stage (je nach Pipeline der
   * Abteilung pb_phases.id, bk_phases.id oder department_stages.id).
   * Name/Titel liefert das Backend mit (Liste ohne Nachladen beschreibbar).
   */
  assign_department_id?: number | null;
  assign_stage_id?: number | null;
  assign_department_name?: string | null;
  assign_stage_title?: string | null;
  aktiv: boolean;
}

export interface PhaseActionCreatePayload {
  scope: PhaseActionScope;
  phase_id: number;
  trigger: PhaseActionTrigger;
  action_type: PhaseActionType;
  email_template_id?: number | null;
  task_titel?: string | null;
  task_beschreibung?: string | null;
  task_offset_value?: number | null;
  task_offset_unit?: 'hours' | 'days' | null;
  docusign_template_id?: string | null;
  docusign_template_name?: string | null;
  docusign_role?: string | null;
  docusign_recipient?: string | null;
  docusign_internal_role?: string | null;
  docusign_internal_recipient?: string | null;
  notify_channel?: PhaseActionNotifyChannel | null;
  notify_roles?: number[] | null;
  notify_subject?: string | null;
  notify_message?: string | null;
  calendar_titel?: string | null;
  calendar_beschreibung?: string | null;
  calendar_offset_value?: number | null;
  calendar_offset_unit?: 'hours' | 'days' | null;
  calendar_duration_minutes?: number | null;
  calendar_category_id?: number | null;
  jump_target_phase_id?: number | null;
  assign_department_id?: number | null;
  assign_stage_id?: number | null;
}

export const v2PhaseActions = {
  list: async (scope: PhaseActionScope): Promise<PhaseActionRow[]> => {
    const res = await apiV2.get<{ data: PhaseActionRow[] }>('/admin/phase-actions', { params: { scope } });
    return res.data.data;
  },

  create: async (input: PhaseActionCreatePayload): Promise<PhaseActionRow> => {
    const res = await apiV2.post<{ data: PhaseActionRow }>('/admin/phase-actions', input);
    return res.data.data;
  },

  update: async (id: number, input: Partial<PhaseActionCreatePayload> & { aktiv?: boolean }): Promise<PhaseActionRow> => {
    const res = await apiV2.put<{ data: PhaseActionRow }>(`/admin/phase-actions/${id}`, input);
    return res.data.data;
  },

  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/phase-actions/${id}`);
  },

  /**
   * Empfänger-Rollen für die Aktion „Benachrichtigung senden".
   * Eigene Route statt /admin/rbac/roles: die liegt hinter admins.read, der
   * Stage-Editor hinter menu.einstellungen.
   */
  roles: async (): Promise<PhaseActionRole[]> => {
    const res = await apiV2.get<{ data: PhaseActionRole[] }>('/admin/phase-action-roles');
    return res.data.data;
  },
};

/* ─── DocuSign (FA-010) ─────────────────────────────────────────────────── */

export interface DocusignTemplateRole {
  role: string;
  routing_order: string;
  /** Feldnamen der Vorlage; passende Profilwerte füllt das Backend beim Versand. */
  fields: string[];
}

export interface DocusignTemplate {
  template_id: string;
  name: string;
  updated_at: string | null;
  roles: DocusignTemplateRole[];
}

/** Empfänger-Variable für die Stage-Aktion (Kontakttyp aus dem Familienkonto usw.). */
export interface DocusignRecipientType {
  value: string;
  label: string;
  group: 'stage' | 'familienkonto' | 'intern';
}

/**
 * Grund, warum die DocuSign-Vorlagen nicht geladen werden konnten. Der
 * Controller unterscheidet die Fälle (503 = nicht konfiguriert, 502 = DocuSign
 * nicht erreichbar, 403 = fehlende Berechtigung) — die Oberfläche soll sie
 * ebenfalls unterscheiden, statt pauschal „nicht verbunden" zu behaupten.
 */
export type DocusignErrorKind = 'not_configured' | 'unavailable' | 'forbidden' | 'unknown';

export function docusignErrorKind(e: unknown): DocusignErrorKind {
  const err = e as { response?: { status?: number; data?: { errors?: { code?: string }[] } } };
  const status = err?.response?.status;
  const code = err?.response?.data?.errors?.[0]?.code;

  if (code === 'E_DOCUSIGN_NOT_CONFIGURED' || status === 503) return 'not_configured';
  if (code === 'E_DOCUSIGN_UNAVAILABLE' || status === 502) return 'unavailable';
  // 401 ist hier nicht abgedeckt: apiV2 fängt es in handle401Redirect ab und
  // schickt zum Login, bevor eine Meldung gerendert werden könnte.
  if (status === 403) return 'forbidden';
  return 'unknown';
}

export const v2Docusign = {
  /** Vorlagen inkl. Rollen; 503 = DocuSign noch nicht konfiguriert. */
  templates: async (fresh = false): Promise<DocusignTemplate[]> => {
    const res = await apiV2.get<{ data: DocusignTemplate[] }>('/admin/docusign/templates', {
      params: fresh ? { fresh: 1 } : undefined,
    });
    // roles/fields defensiv normalisieren: DocuSign lässt `recipients`/`tabs`
    // in Vorlagen ohne Empfänger bzw. ohne Datenfelder weg, und die Sektion
    // rendert sonst gar nichts mehr (roles.length auf undefined).
    return (res.data.data ?? []).map(tpl => ({
      ...tpl,
      roles: (tpl.roles ?? []).map(r => ({ ...r, fields: r.fields ?? [] })),
    }));
  },

  recipientTypes: async (): Promise<DocusignRecipientType[]> => {
    const res = await apiV2.get<{ data: DocusignRecipientType[] }>('/admin/docusign/recipient-types');
    return res.data.data ?? [];
  },

  /**
   * Auswahllisten für den manuellen Versand aus dem Profil. Eigene Routen unter
   * users.write, weil die Einstellungs-Routen menu.einstellungen verlangen.
   */
  sendTemplates: async (fresh = false): Promise<DocusignTemplate[]> => {
    const res = await apiV2.get<{ data: DocusignTemplate[] }>('/admin/docusign/send/templates', {
      params: fresh ? { fresh: 1 } : undefined,
    });
    return (res.data.data ?? []).map(tpl => ({
      ...tpl,
      roles: (tpl.roles ?? []).map(r => ({ ...r, fields: r.fields ?? [] })),
    }));
  },

  sendRecipientTypes: async (): Promise<DocusignRecipientType[]> => {
    const res = await apiV2.get<{ data: DocusignRecipientType[] }>('/admin/docusign/send/recipient-types');
    return res.data.data ?? [];
  },

  /** Vertrag manuell aus dem Profil versenden (Tab „Medien & Dokumente"). */
  send: async (userId: number, input: DocusignSendInput): Promise<DocusignEnvelopeRow> => {
    const res = await apiV2.post<{ data: DocusignEnvelopeRow }>(`/admin/users/${userId}/docusign/envelopes`, input);
    return res.data.data;
  },

  /** Versendete DocuSign-Dokumente eines Profils, neueste zuerst. */
  envelopes: async (userId: number): Promise<DocusignEnvelopeRow[]> => {
    const res = await apiV2.get<{ data: DocusignEnvelopeRow[] }>('/admin/docusign/envelopes', {
      params: { user_id: userId, per_page: 50 },
    });
    return res.data.data ?? [];
  },
};

export interface DocusignSendInput {
  template_id: string;
  template_name?: string | null;
  role: string;
  recipient?: string | null;
  internal_recipient?: string | null;
}

export interface DocusignEnvelopeRow {
  id: number;
  envelope_id: string;
  user_id: number | null;
  template_name: string | null;
  role: string | null;
  recipient_name: string | null;
  recipient_email: string | null;
  recipient_type: string | null;
  /** sent | delivered | completed | declined | voided */
  status: string;
  sent_at: string | null;
  completed_at: string | null;
}

/** Fehlertext des Backends beim Versand (z. B. „Im Familienkonto gibt es keine Person …"). */
export function docusignSendError(e: unknown): string | null {
  const err = e as { response?: { data?: { errors?: { code?: string; message?: string }[] } } };
  const first = err?.response?.data?.errors?.[0];
  return first?.code === 'E_DOCUSIGN_SEND_FAILED' && first.message ? first.message : null;
}

/** Liest die unerfüllten Bedingungen aus einer E_PHASE_RULES-422-Antwort. */
export function phaseRuleErrors(e: unknown): string[] | null {
  const err = e as { response?: { data?: { errors?: { code?: string; unmet?: string[] }[] } } };
  const first = err?.response?.data?.errors?.[0];
  if (first?.code === 'E_PHASE_RULES' && Array.isArray(first.unmet)) return first.unmet;
  return null;
}

/* ─── Task & Notice mutations ───────────────────────────────────────────── */

export interface V1Task {
  id: number;
  user_id?: number | null;
  /** Ersteller — Admin-ID (oder Legacy-String). */
  von?: number | string | null;
  /** Bearbeiter — JSON-Array von Admin-IDs (Assignees). */
  bearbeiter?: number[] | null;
  titel?: string;
  beschreibung?: string;
  datum?: string | null;
  erinnerung?: string | null;
  zieldatum?: string | null;
  status?: number;
  /** Priorität: 0 = Normal, 1 = Hoch (Alarm-Rot). */
  priority?: number;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** Zeile aus GET /v1/admin/tasks (globale Aufgabenliste). */
export interface V1DashboardTask extends V1Task {
  /** Admin-Gruppen-IDs als Bearbeiter. */
  bearbeiter_gruppen?: number[] | null;
  /** Kommagetrennte Gruppennamen (serverseitig aufgelöst). */
  gruppen?: string;
  /** Name der betroffenen Person (PB/BK) — additiv vom Backend geliefert. */
  user_name?: string | null;
  /** Rolle der betroffenen Person: 1 = BK, 2 = PB. */
  user_role?: number | null;
}

export interface V1Notice {
  id: number;
  user_id?: number | null;
  admin_id?: number | null;
  comment?: string;
  nachricht?: string;
  text?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export const v1Tasks = {
  /**
   * POST /v1/admin/task — creates a new task.
   * Required: titel, beschreibung. Optional: user_id, zieldatum, status.
   *
   * `von` = Ersteller (Admin-ID), `bearbeiter` = Assignees (Admin-ID-Array).
   * Beide werden nur mitgeschickt, wenn gesetzt — der Backend-Endpoint
   * akzeptiert sie additiv.
   */
  create: async (payload: {
    user_id?: number;
    titel: string;
    beschreibung: string;
    zieldatum?: string | null;
    priority?: number;
    status?: number;
    von?: number | null;
    bearbeiter?: number[] | null;
  }): Promise<V1Task> => {
    const res = await apiV1.post<{ type: string; task: V1Task }>('/admin/task', payload);
    return res.data.task;
  },

  /**
   * PUT /v1/admin/task/{id} — updates task (titel + beschreibung required by validator).
   * Pass status to toggle done/undone.
   * `bearbeiter` (Assignee-IDs) wird additiv durchgereicht, wenn gesetzt.
   */
  update: async (id: number, payload: {
    titel: string;
    beschreibung: string;
    status?: number;
    priority?: number;
    zieldatum?: string | null;
    bearbeiter?: number[] | null;
  }): Promise<V1Task> => {
    const res = await apiV1.put<{ type: string; task: V1Task }>(`/admin/task/${id}`, payload);
    return res.data.task;
  },

  /**
   * DELETE /v1/admin/task/{id} — hard-deletes a task and its activities.
   */
  delete: async (id: number): Promise<void> => {
    await apiV1.delete(`/admin/task/${id}`);
  },
};

export const v1Notices = {
  /**
   * PUT /v1/admin/notice/{id}/pin — schaltet „angepinnt" um (CRM-1.0-Endpunkt,
   * unverändert). Liefert die Notizen der Person, gepinnte zuerst.
   */
  pin: async (id: number): Promise<V1Notice[]> => {
    const res = await apiV1.put<{ type: string; notices: V1Notice[] }>(`/admin/notice/${id}/pin`);
    return res.data.notices ?? [];
  },

  /**
   * GET /v1/admin/user/{id}/notices — all notices for a user, pinned-first
   * then newest-updated-first. Used by AufgabenNotizenTab to reload after
   * create/update/delete without depending on the parent user fetch.
   */
  listForUser: async (userId: number): Promise<V1Notice[]> => {
    const res = await apiV1.get<{ type: string; notices: V1Notice[] }>(
      `/admin/user/${userId}/notices`,
    );
    return res.data.notices ?? [];
  },

  /**
   * POST /v1/admin/notice — creates a new notice/internal note.
   * Required: comment. Optional: user_id.
   */
  create: async (payload: {
    user_id?: number;
    comment: string;
  }): Promise<V1Notice> => {
    const res = await apiV1.post<{ type: string; notice: V1Notice }>('/admin/notice', payload);
    return res.data.notice;
  },

  /**
   * PUT /v1/admin/notice/{id} — updates notice text.
   * Required: comment.
   */
  update: async (id: number, payload: { comment: string }): Promise<V1Notice> => {
    const res = await apiV1.put<{ type: string; notice: V1Notice }>(`/admin/notice/${id}`, payload);
    return res.data.notice;
  },

  /**
   * DELETE /v1/admin/notice/{id} — hard-deletes a notice.
   */
  delete: async (id: number): Promise<void> => {
    await apiV1.delete(`/admin/notice/${id}`);
  },
};

/* ─── Abwesenheiten (Punkt 5) ───────────────────────────────────────────── */

export interface V1Absence {
  id: number;
  user_id: number;
  admin_id?: number | null;
  grund: string;
  startdatum: string;
  /** Optional — eine Abwesenheit kann offen (ohne Enddatum) sein. */
  enddatum: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** Fehler der Abwesenheits-Endpunkte mit API-Meldung und -Code (z. B. E_ABSENCE_OVERLAP). */
export class AbsenceApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'AbsenceApiError';
    this.code = code;
  }
}

function absenceApiError(err: unknown): unknown {
  const data = (err as { response?: { data?: { message?: string; code?: string } } })?.response?.data;
  if (data?.message) return new AbsenceApiError(data.message, data.code);
  return err;
}

/**
 * E_ABSENCE_OVERLAP nennt in der API-Meldung den Grund der bestehenden
 * Abwesenheit („(Urlaub, 01.10.2026 bis 20.10.2026)"). Für BK gibt es keine
 * Art mehr: Zeitraum aus der Meldung ziehen, den Grund weglassen.
 */
export function absenceOverlapPeriod(message: string): { von: string; bis: string } | null {
  const m = /(\d{2}\.\d{2}\.\d{4}) bis (\d{2}\.\d{2}\.\d{4}|offen)/.exec(message);
  return m ? { von: m[1], bis: m[2] } : null;
}

export const v1Absences = {
  /** GET /v1/admin/user/{id}/absences → all absences for a PB, newest first. */
  listForUser: async (userId: number): Promise<V1Absence[]> => {
    const res = await apiV1.get<{ type: string; absences: V1Absence[] }>(
      `/admin/user/${userId}/absences`,
    );
    return res.data.absences ?? [];
  },

  /** POST /v1/admin/absence — create. Required: user_id, grund, startdatum. enddatum optional. */
  create: async (payload: {
    user_id: number;
    grund: string;
    startdatum: string;
    enddatum?: string | null;
  }): Promise<V1Absence> => {
    try {
      const res = await apiV1.post<{ type: string; absence: V1Absence; message?: string }>('/admin/absence', payload);
      if (res.data.type === 'error') throw new Error(res.data.message ?? 'Abwesenheit konnte nicht angelegt werden.');
      return res.data.absence;
    } catch (err) {
      // Surface 422 API error messages (e.g. E_VERTRAGSPAUSE_IN_CARE).
      throw absenceApiError(err);
    }
  },

  /** PUT /v1/admin/absence/{id} — update grund/startdatum/enddatum (enddatum optional). */
  update: async (id: number, payload: {
    grund: string;
    startdatum: string;
    enddatum?: string | null;
  }): Promise<V1Absence> => {
    try {
      const res = await apiV1.put<{ type: string; absence: V1Absence; message?: string }>(`/admin/absence/${id}`, payload);
      if (res.data.type === 'error') throw new Error(res.data.message ?? 'Abwesenheit konnte nicht aktualisiert werden.');
      return res.data.absence;
    } catch (err) {
      throw absenceApiError(err);
    }
  },

  /** DELETE /v1/admin/absence/{id}. */
  delete: async (id: number): Promise<void> => {
    await apiV1.delete(`/admin/absence/${id}`);
  },
};

/**
 * Anzeige-Label für den Abwesenheitsgrund. Der `grund` wird kanonisch als
 * deutscher Wert gespeichert (Krankenhaus/Urlaub); hier auf das übersetzte
 * Label gemappt, mit Fallback auf den Rohwert (z.B. Alt-Freitext).
 */
export function absenceGrundLabel(grund: string | null | undefined, t: (k: string) => string): string {
  const g = (grund ?? '').trim();
  if (g === 'Krankenhaus') return t('pflege.absences.grund_options.krankenhaus');
  if (g === 'Urlaub') return t('pflege.absences.grund_options.urlaub');
  if (g === 'Abwesenheit') return t('betreuung.availability.type.abwesenheit');
  if (g === 'Vertragspause') return t('pflege.absences.grund_options.vertragspause');
  // Back-compat: rows not yet migrated still render with the new label.
  if (g === 'Pause') return t('pflege.absences.grund_options.vertragspause');
  // BK-Verfügbarkeit (Dispo) — gleiche Tabelle, eigene Gründe.
  if (g === 'Verfügbar') return t('betreuung.availability.type.verfuegbar');
  if (g === 'Kein Kontakt') return t('betreuung.availability.type.kein_kontakt');
  return g;
}

export interface V1UploadRow {
  id: number;
  user_id?: number;
  kategorie: string;
  datei: string;
  typ?: string;
  groesse?: number;
  filename?: string | null;
}

export interface V1DocumentRow {
  id: number;
  user_id?: number;
  kategorie: string;
  name?: string | null;
  datei: string;
  typ?: string;
  groesse?: number;
}

export const v1Uploads = {
  /**
   * POST /v1/admin/upload (multipart/form-data)
   * Fields: files[] (jpeg/jpg/png/mp4/webm), user_id, kategorie
   * Returns { type: 'success', uploads: Upload[] }
   */
  uploadMedia: async (userId: number, file: File, kategorie: string): Promise<V1UploadRow> => {
    const fd = new FormData();
    fd.append('files[]', file);
    fd.append('user_id', String(userId));
    fd.append('kategorie', kategorie);
    const res = await apiV1.post<{ type: string; uploads: V1UploadRow[] }>(
      '/admin/upload',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    if (res.data.type !== 'success' || !res.data.uploads?.length) {
      throw new Error((res.data as unknown as { message?: string }).message ?? i18n.t('pflege.detail.dashboard.upload_error'));
    }
    return res.data.uploads[0];
  },

  /**
   * POST /v1/admin/document (multipart/form-data)
   * Fields: files[] (pdf/doc/docx/xls/xlsx/jpeg/jpg/png/mp4/webm), user_id, kategorie
   * Returns { type: 'success', documents: Document[] }
   */
  uploadDocument: async (userId: number, file: File, kategorie: string): Promise<V1DocumentRow> => {
    const fd = new FormData();
    fd.append('files[]', file);
    fd.append('user_id', String(userId));
    fd.append('kategorie', kategorie);
    const res = await apiV1.post<{ type: string; documents: V1DocumentRow[] }>(
      '/admin/document',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    if (res.data.type !== 'success' || !res.data.documents?.length) {
      throw new Error((res.data as unknown as { message?: string }).message ?? i18n.t('pflege.detail.dashboard.upload_error'));
    }
    return res.data.documents[0];
  },
};

export const v1Contacts = {
  /** GET /v1/admin/contactTypes → editable Ansprechpartner labels/types. */
  types: async (): Promise<V1ContactType[]> => {
    const res = await apiV1.get<{ type: string; options: V1ContactType[] }>('/admin/contactTypes');
    return res.data.options;
  },

  /** GET /v1/admin/user/{id}/contacts → paginated Ansprechpartner/Kontakte for this user. */
  listForUser: async (id: number, params?: ListParams) => {
    const res = await apiV1.get<{ type: string; pagination?: LaravelPagination<V1Contact>; contacts?: V1Contact[] }>(
      `/admin/user/${id}/contacts`,
      { params: buildParams(params) },
    );
    if (res.data.pagination) return res.data.pagination;
    return {
      current_page: 1,
      data: res.data.contacts ?? [],
      first_page_url: '',
      from: (res.data.contacts?.length ?? 0) > 0 ? 1 : null,
      last_page: 1,
      last_page_url: '',
      next_page_url: null,
      path: '',
      per_page: res.data.contacts?.length ?? 0,
      prev_page_url: null,
      to: res.data.contacts?.length ?? null,
      total: res.data.contacts?.length ?? 0,
    } satisfies LaravelPagination<V1Contact>;
  },

  /** PUT /v1/admin/contact/{id} → update one Ansprechpartner/Kontakt row. */
  update: async (id: number, payload: Record<string, unknown>): Promise<V1Contact> => {
    const res = await apiV1.put<{ type: string; contact: V1Contact }>(`/admin/contact/${id}`, payload);
    return res.data.contact;
  },

  /**
   * POST /v1/admin/contact → neuen Ansprechpartner anlegen.
   * Payload braucht mindestens `user_id`; Backend (AdminController::createContact)
   * prüft email/mobile auf Eindeutigkeit gegenüber anderen Usern.
   */
  create: async (payload: Record<string, unknown>): Promise<V1Contact> => {
    const res = await apiV1.post<{ type: string; message?: string; contact?: V1Contact }>('/admin/contact', payload);
    if (res.data.type === 'error' || !res.data.contact) {
      throw new Error(res.data.message ?? 'Kontakt konnte nicht angelegt werden.');
    }
    return res.data.contact;
  },

  /**
   * DELETE /v1/admin/contact/{id} → Ansprechpartner löschen.
   * Backend verweigert Löschen, wenn der Kontakt als Account-Login dient
   * (liefert dann type=error mit Meldung).
   */
  remove: async (id: number): Promise<void> => {
    const res = await apiV1.delete<{ type: string; message?: string }>(`/admin/contact/${id}`);
    if (res.data.type === 'error') throw new Error(res.data.message ?? 'Kontakt konnte nicht gelöscht werden.');
  },
};

/** Profiles — admin endpoint for the extended PB/BK profile row. */
export const v1Profiles = {
  /**
   * PUT /v1/admin/profile/{id} — partial update. The Profile model casts
   * many fields (pflegegrad, einschraenkungen, …) to JSON, so arrays may
   * be sent directly; Laravel will encode them.
   */
  update: async (id: number, payload: Record<string, unknown>): Promise<V1Profile> => {
    const res = await apiV1.put<{ type: string; profile: V1Profile }>(`/admin/profile/${id}`, payload);
    return res.data.profile;
  },
};

/** Profile options (familienstand, pflegegrad, einschraenkungen, …) — admin-editable enum table. */
export interface V1ProfileOption {
  id: number;
  name: string;   // field key, e.g. "pflegegrad"
  value: string;  // display label, e.g. "Pflegegrad 3"
  /**
   * Befund 23.09.: additive Spalte (Migration
   * 2026_09_23_100000_add_is_selectable_to_profile_options_table) — false
   * blendet die Option aus NEUEN Dropdown-Auswahlen aus (z.B. profiltyp
   * "Frau"/"Mann"), ohne bestehende gespeicherte Werte zu verstecken oder
   * ihr Speichern zu verhindern. Undefined (ältere API-Antworten ohne das
   * Feld) verhält sich wie `true`.
   */
  is_selectable?: boolean;
}

export const v1ProfileOptions = {
  /** GET /v1/admin/profileOptions → all options (flat list). */
  list: async (): Promise<V1ProfileOption[]> => {
    const res = await apiV1.get<{ type: string; options: V1ProfileOption[] }>(
      '/admin/profileOptions',
    );
    return res.data.options;
  },

  /** Convenience: group flat options by their `name` field key. */
  grouped: async (): Promise<Record<string, V1ProfileOption[]>> => {
    const flat = await v1ProfileOptions.list();
    return flat.reduce<Record<string, V1ProfileOption[]>>((acc, opt) => {
      (acc[opt.name] ||= []).push(opt);
      return acc;
    }, {});
  },
};

/** Kriterien der Bewertung eines PB (ratings_bk): je 1–5 Sterne. */
export interface V2PbRatingScores {
  rating_wohnqualitaet: number;
  rating_charakter: number;
  rating_pflegeaufwand: number;
  rating_umfeld: number;
  rating_freizeit: number;
}

/**
 * Kriterien der Bewertung einer BK durch den Kunden (Tabelle ratings).
 * „Zusammenarbeit" liegt in der Spalte rating_kommunikation (siehe
 * pages/pflege/tabs/ratingCriteria.ts).
 */
export interface V2BkRatingScores {
  rating_erscheinungsbild: number;
  rating_aufgeschlossenheit: number;
  rating_kommunikation: number;
  rating_kompetenz: number;
  rating_haushalt: number;
  rating_sprachkenntnisse: number;
}

/** Bewertung anlegen (v2): bewertende Person = Profil ODER Freitext-Name. */
export type V2RatingCreate = (V2PbRatingScores | V2BkRatingScores) & {
  /** Verknuepftes Profil der bewertenden Person (BK bei PB, PB bei BK) — oder stattdessen `von_name`. */
  von_user_id?: number | null;
  /** Freitext-Name der bewertenden Person (ohne Profilbezug). */
  von_name?: string | null;
  rating_text?: string | null;
  /** Y-m-d H:i:s, ohne Angabe jetzt. */
  created_at?: string;
};

/** Zeile der v2-Bewertungslisten. */
export interface V2RatingRow {
  id: number;
  user_id: number;
  von_user_id: number;
  von_name: string | null;
  von_linked: boolean;
  fuer_name: string | null;
  care_id?: number | null;
  rating_gesamt?: number | null;
  rating_text?: string | null;
  created_at?: string;
  [field: string]: unknown;
}

/**
 * Aktive Mitarbeiter:innen (admins.konto === 1, wie CRM 1.0 „Konto aktiv").
 * Damon 11.10.2026: In allen Mitarbeiter-Auswahlen stehen nur aktive. Für die
 * Namensauflösung bleibt die volle Liste maßgeblich.
 */
export function isActiveAdmin(a: Pick<V1Admin, 'konto'>): boolean {
  return Number(a.konto) === 1;
}

/**
 * Auswahl-Optionen: nur aktive, plus bereits zugewiesene (`keepIds`), damit
 * ein inaktiv gewordener Eintrag im Feld weiter mit Namen erscheint.
 */
export function selectableAdmins<T extends Pick<V1Admin, 'id' | 'konto'>>(admins: T[], keepIds: Array<number | null | undefined> = []): T[] {
  const keep = new Set(keepIds.filter((x): x is number => x != null).map(Number));
  return admins.filter(a => isActiveAdmin(a) || keep.has(Number(a.id)));
}

/** Admins alphabetisch nach Vorname, dann Nachname (deutsche Sortierung). */
export function sortAdminsByFirstName<T extends Pick<V1Admin, 'vorname' | 'nachname'>>(admins: T[]): T[] {
  const key = (a: T) => [(a.vorname ?? '').trim(), (a.nachname ?? '').trim()];
  return [...admins].sort((a, b) => {
    const [av, an] = key(a);
    const [bv, bn] = key(b);
    return av.localeCompare(bv, 'de', { sensitivity: 'base' }) || an.localeCompare(bn, 'de', { sensitivity: 'base' });
  });
}

/** Admin self-service: load + update the currently logged-in admin's profile. */
export const v1Admins = {
  /**
   * GET /v1/admin/all-admins → flat list of all CRM admins (no pagination).
   * Alphabetisch nach Vorname (dann Nachname) sortiert — alle Auswahllisten
   * wie „Zugewiesen an" bauen auf dieser Liste auf.
   */
  list: async (): Promise<V1Admin[]> => {
    const res = await apiV1.get<{ type: string; admins: V1Admin[] }>('/admin/all-admins');
    return sortAdminsByFirstName(res.data.admins ?? []);
  },

  /** GET /v1/admin/admins → paginated Admin list. */
  paginated: async (params?: ListParams): Promise<LaravelPagination<V1Admin>> => {
    const res = await apiV1.get<PaginatedEnvelope<V1Admin>>(
      '/admin/admins',
      { params: buildParams(params) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/adminRoles → all admin roles (id + name); matches RBAC role names. */
  roles: async (): Promise<V1AdminRole[]> => {
    const res = await apiV1.get<{ type: string; options: V1AdminRole[] }>('/admin/adminRoles');
    return res.data.options ?? [];
  },

  /** GET /v1/admin/admin/{id} → single Admin record (no profile_image bytes). */
  get: async (id: number): Promise<V1Admin> => {
    const res = await apiV1.get<{ type: string; admin: V1Admin }>(`/admin/admin/${id}`);
    return res.data.admin;
  },

  /**
   * PUT /v1/admin/admin/{id} — backend validator requires konto+role+email+vorname+nachname,
   * so callers must round-trip those fields (don't omit konto/role just because UI doesn't
   * surface them).
   */
  update: async (id: number, payload: V1AdminUpdatePayload): Promise<V1Admin> => {
    const res = await apiV1.put<{ type: string; admin: V1Admin }>(`/admin/admin/${id}`, payload);
    return res.data.admin;
  },

  /** POST /v1/admin/admin → create new Admin. */
  create: async (payload: V1AdminUpdatePayload & { password: string }): Promise<V1Admin> => {
    const res = await apiV1.post<{ type: string; admin: V1Admin }>('/admin/admin', payload);
    return res.data.admin;
  },

  /** DELETE /v1/admin/admin/{id} → deactivate/delete Admin. */
  delete: async (id: number): Promise<void> => {
    await apiV1.delete(`/admin/admin/${id}`);
  },
};

/** Admin messaging inbox used by EmailPage. */
/* ─── Kunden-Chat (CRM-1.0-App-Chat, chats-Tabelle) ────────────────────────
 *
 * POST/GET /v1/admin/chat/{userId} + /updates-Polling + /chat-read.
 * `von` = Admin-ID (String) beim Admin, 'user' beim Kunden.
 */
export interface V1Chat {
  id: number;
  user_id: number;
  von: string | number;
  typ: string;
  inhalt: string | null;
  datei: string | null;
  gelesen: number | boolean;
  created_at: string;
}

export const v1Chat = {
  /** Verlauf der letzten 90 Tage (aufsteigend). */
  list: async (userId: number): Promise<V1Chat[]> => {
    const res = await apiV1.get<{ type: string; chats: V1Chat[] }>(`/admin/chat/${userId}`);
    return res.data.chats;
  },

  /**
   * Neue Nachrichten seit `since` (Poll). Läuft über v2: v1 /chat/{id}/updates
   * verglich den UTC-Zeitstempel als Berliner Ortszeit und lieferte bei jedem
   * Poll bereits bekannte Nachrichten erneut.
   */
  updates: async (userId: number, since: string): Promise<V1Chat[]> => {
    const res = await apiV2.get<{ data: V1Chat[] }>(`/admin/users/${userId}/chat/updates`, { params: { since } });
    return res.data.data;
  },

  /**
   * Textnachricht an den Kunden senden. Läuft über v2: v1 antwortete mit 500,
   * sobald der App-Push scheiterte (Nachricht war trotzdem gespeichert). v2
   * schickt den Push best-effort und meldet ihn in `meta.push_sent`.
   */
  send: async (userId: number, inhalt: string): Promise<V1Chat> => {
    const res = await apiV2.post<{ data: V1Chat; meta: { push_sent?: boolean } }>(`/admin/users/${userId}/chat`, { inhalt });
    return res.data.data;
  },

  /** Alle Nachrichten des Users als gelesen markieren. */
  markRead: async (userId: number): Promise<void> => {
    await apiV1.put(`/admin/chat-read/${userId}`);
  },
};

export const v1Messages = {
  /** GET /v1/admin/messages → paginated Message. */
  list: async (params?: ListParams) => {
    const filters = { showSystemMails: 'false', ...(params?.filters ?? {}) };
    const res = await apiV1.get<PaginatedEnvelope<V1Message>>(
      '/admin/messages',
      { params: buildParams({ ...params, filters }) },
    );
    return res.data.pagination;
  },

  /** GET /v1/admin/messages/{id} → single Message detail. */
  get: async (id: number): Promise<V1Message> => {
    const res = await apiV1.get<{ type: string; message: V1Message }>(
      `/admin/messages/${id}`,
    );
    return res.data.message;
  },

  /**
   * POST /v1/admin/message — sends a message to a user.
   * Dispatched via queue (SendMessage job). Required by the validator:
   *   empfaenger (Empfänger-E-Mail), empfaenger_id (User.id), betreff, nachricht, smtp.
   * smtp defaults to 'smtp' (German sender).
   */
  send: async (payload: {
    user_id: number;
    empfaenger: string;
    empfaenger_id: number;
    betreff: string;
    nachricht: string;
    smtp?: string;
    absender?: string;
    template?: number | null;
    anhang?: number[] | null;
  }): Promise<{ type: string; message: string }> => {
    const body = { smtp: 'smtp', ...payload };
    const res = await apiV1.post<{ type: string; message: string }>('/admin/message', body);
    // v1 meldet Validierungsfehler mit HTTP 200 und type 'error'.
    if (res.data?.type === 'error') throw new Error(res.data.message || 'message_failed');
    return res.data;
  },
};

/* ─── Mappers v1 → UI types ─────────────────────────────────────────── */

const ANREDE_LABEL: Record<string, string> = { '1': 'Herr', '2': 'Frau' };

function fullName(u: V1User): string {
  if (u.fullname) return u.fullname;
  const direct = [u.vorname, u.nachname].filter(Boolean) as string[];
  if (direct.length) return direct.join(' ');
  // Fall back to primary profile (getUsersByRole attaches name there, not on User).
  const p = primaryProfile(u);
  const fromProfile = [p?.vorname, p?.nachname].filter(Boolean) as string[];
  if (fromProfile.length) return fromProfile.join(' ');
  return u.email ?? `User #${u.id}`;
}

/** Relative German date label ("vor 2 Std", "gestern", "3 Tage"). */
/**
 * Alter in vollen Jahren aus einem Geburtsdatum (ISO `YYYY-MM-DD`, auch mit
 * Zeitanteil, oder `DD.MM.YYYY`). 0, wenn kein/ungültiges Datum — Aufrufer
 * zeigen das Alter dann nicht an.
 */
export function ageFromBirth(birth: string | null | undefined, today: Date = new Date()): number {
  if (!birth) return 0;
  const raw = String(birth).trim();
  let y: number, m: number, d: number;
  const de = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (de) { d = Number(de[1]); m = Number(de[2]); y = Number(de[3]); }
  else if (iso) { y = Number(iso[1]); m = Number(iso[2]); d = Number(iso[3]); }
  else return 0;
  if (!y || !m || !d) return 0;
  let age = today.getFullYear() - y;
  const beforeBirthday = (today.getMonth() + 1) < m || ((today.getMonth() + 1) === m && today.getDate() < d);
  if (beforeBirthday) age -= 1;
  return age > 0 && age < 130 ? age : 0;
}

/**
 * Relatives Eingangs-Datum ("vor 2 Std", "gestern", "3 Tage") — übersetzt via
 * i18n (reuses the `anfragen_bk.relative_time.*` keys, identischer deutscher
 * Text). `Anfrage.received` bleibt ein `string` (geteilter Typ mit
 * src/lib/mockData.ts), daher wird hier — anders als beim BK-Pendant
 * (bkRelativeTimeKey in anfragenConstants) — direkt zur Abrufzeit über die
 * aktuelle UI-Sprache aufgelöst statt den Key fürs Rendering durchzureichen.
 */
function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return i18n.t('anfragen_bk.relative_time.just_now');
  if (min < 60) return i18n.t('anfragen_bk.relative_time.minutes', { count: min });
  const h = Math.floor(min / 60);
  if (h < 24) return i18n.t('anfragen_bk.relative_time.hours', { count: h });
  const d = Math.floor(h / 24);
  if (d === 1) return i18n.t('anfragen_bk.relative_time.yesterday');
  if (d < 7) return i18n.t('anfragen_bk.relative_time.days', { count: d });
  const w = Math.floor(d / 7);
  return i18n.t('anfragen_bk.relative_time.weeks', { count: w });
}

/**
 * v1 stores pflegegrad with a +4 offset (see getUsersByRole filter math:
 * `['pflegegrad', '>=', $value + 4]`). Clamp into the 1-5 UI range.
 */
function pflegegradFromRaw(raw: number | null | undefined): Pflegegrad {
  if (!raw) return 1;
  const lvl = raw > 4 ? raw - 4 : raw;
  return (Math.min(5, Math.max(1, lvl)) as Pflegegrad);
}

/** Maps `CareRequest.status` (BK↔PB matching request) to UI Stage. */
const STAGE_BY_STATUS: Record<number, Stage> = {
  0: 'Neu',
  1: 'Qualifizierung',
  2: 'Matching',
  3: 'Angebot',
  4: 'Vermittelt',
};

/**
 * Maps `User.konto` (PB onboarding state) to UI Stage:
 *   1 = signup incomplete    → Neu
 *   2 = new lead, admin todo → Qualifizierung
 *   3 = translation pending  → Matching
 *   4 = active customer      → Vermittelt
 */
const STAGE_BY_PB_KONTO: Record<number, Stage> = {
  0: 'Neu',
  1: 'Neu',
  2: 'Qualifizierung',
  3: 'Matching',
  4: 'Vermittelt',
};

function urgencyFromPflegegrad(pg: Pflegegrad): Urgency {
  if (pg >= 4) return 'Hoch';
  if (pg >= 2) return 'Mittel';
  return 'Niedrig';
}

/**
 * Map a new PB user (user_role=2, konto=2) to the UI Anfrage shape — useful
 * for the Dashboard "Recent Anfragen" panel, since `getNewUsers` returns the
 * fresh leads but `CareRequest` itself has no profile join.
 */
export function v1UserToAnfrage(u: V1User): Anfrage {
  const p = primaryProfile(u);
  const nachname = p?.nachname ?? null;
  // Salutation prefix: 'Herr' for male, 'Frau' for female, default 'Familie'.
  // v1 stores anrede as a string but legacy rows may persist it as a number.
  const anrede = p?.anrede == null ? null : String(p.anrede);
  const prefix =
    anrede === '1' ? 'Herr'
    : anrede === '2' ? 'Frau'
    : 'Familie';
  // Hauptkontakt ist der Pflegeempfänger (analog v1UserToClient): ist diese
  // Zeile im Familienkonto nur eine Kontaktperson mit eigenem Account
  // (pe_user_id ≠ id, s. PbListController::enrichRows), zeigen Name UND
  // Pflegegrad von Kachel/Tabellenzeile die Werte des Pflegeempfängers statt
  // die der eigenen (i.d.R. leeren) Kontaktpersonen-Zeile. Ohne pe_* wie
  // bisher. Befund E2E 22.09.2026 (A-22957): Liste zeigte "PG 1" (eigenes,
  // leeres Profil), der Drawer (eigener Nachlade-Request) korrekt
  // "Pflegegrad 3" (Pflegeempfänger) — enrichRows() liefert den PE-Pflegegrad
  // seither additiv mit (pe_pflegegrad), ohne dass die Liste nachladen muss.
  const peOverride = u.pe_user_id != null && u.pe_user_id !== u.id && !!u.pe_name;
  const peName = peOverride ? (u.pe_name as string) : null;
  const pg = peOverride ? pflegegradFromRaw(u.pe_pflegegrad) : pflegegradFromRaw(p?.pflegegrad);
  // Kontakt-Spalte ist der Ansprechpartner (contact_name — vom Backend als
  // Familienkonto-Mitglied OHNE is_pflegeempfaenger aufgelöst, s.
  // PbListController::enrichRows), NICHT der Pflegeempfänger (der steht in
  // `name`). Fallback wie vor Batch-13/c5da9bd: das eigene Profil dieser
  // Zeile, wenn (noch) kein contact_name vorliegt.
  const contactName = typeof u.contact_name === 'string' && u.contact_name ? u.contact_name : null;
  return {
    id: `A-${u.id}`,
    name: peName ?? (nachname ? `${prefix} ${nachname}` : (u.email ?? `Anfrage #${u.id}`)),
    contact: contactName ?? ([p?.vorname, p?.nachname].filter(Boolean).join(' ') || '—'),
    phone: u.mobile ?? p?.telefon ?? '',
    email: u.email ?? '',
    city: peOverride ? (u.pe_ort ?? '') : (p?.ort ?? ''),
    urgency: urgencyFromPflegegrad(pg),
    care_level: `Pflegegrad ${pg}`,
    need: '—',
    note: '',
    received: relativeTime(u.created_at),
    source: 'Onboarding',
    stage: STAGE_BY_PB_KONTO[u.konto] ?? 'Neu',
    budget: '—',
    assigned_to: typeof u.assigned_to === 'number' ? u.assigned_to : (u.assigned_to ?? null),
    assigned_to_name: typeof u.assigned_admin_name === 'string' ? u.assigned_admin_name : '',
    wiedervorlage_am: u.wiedervorlage_am ?? null,
    wiedervorlage_notiz: u.wiedervorlage_notiz ?? null,
  };
}

/**
 * v1 has no UI concept of `urgency / need / budget / stage`. Defaults are
 * derived from pflegegrad + request.status; pages may override.
 */
export function v1RequestToAnfrage(req: V1CareRequest, profile?: V1Profile | null): Anfrage {
  const pg = pflegegradFromRaw(profile?.pflegegrad);
  const nachname = profile?.nachname ?? null;
  return {
    id: `A-${req.id}`,
    name: nachname ? `Familie ${nachname}` : `Anfrage #${req.id}`,
    contact: [profile?.vorname, profile?.nachname].filter(Boolean).join(' ') || '—',
    phone: profile?.telefon ?? '',
    email: '',
    city: profile?.ort ?? '',
    urgency: urgencyFromPflegegrad(pg),
    care_level: `Pflegegrad ${pg}`,
    need: '—',
    note: '',
    received: relativeTime(req.created_at),
    source: 'CRM',
    stage: STAGE_BY_STATUS[req.status ?? 0] ?? 'Neu',
    budget: '—',
  };
}

/** Map a BK user (user_role=1) to the UI Caregiver type. */
export function v1UserToCaregiver(u: V1User): Caregiver {
  const p = primaryProfile(u);
  return {
    id: `BK-${u.id}`,
    name: fullName(u),
    role: 'Betreuungskraft',
    certifications: [],
    languages: [],
    location: p?.ort ?? '—',
    availability: '—',
    clients: 0,
    rating: 0,
    reviews: 0,
    since: formatDate(u.created_at, ''),
    status: u.konto === 4 ? 'Aktiv' : u.konto === 2 ? 'Verfügbar' : 'Inaktiv',
    phone: String(u.telefon ?? p?.telefon ?? ''),
    mobile: u.mobile ?? '',
    email: u.email ?? '',
    bio: '',
    assignments: [],
    // Felder der alten Admin-BK-Liste (UserList.js) — getUsersByRole hängt
    // currentContract (prices_bk.vertrag des aktiven Preises) und
    // assigned_admin_name pro Zeile an; Rest liegt direkt auf users/profiles.
    contract: typeof u.currentContract === 'string' && u.currentContract !== '' ? u.currentContract : null,
    arbeitsstatus: typeof u.status === 'number' ? u.status : null,
    invisible: u.invisible === 1 || u.invisible === true,
    pflegegrad: p?.pflegegrad ?? null,
    land: (typeof u.land === 'string' && u.land) ? u.land : (p?.land ?? null),
    startdatum: u.startdatum ?? null,
    origin: u.origin == null || u.origin === '' ? null : String(u.origin),
    assigned_to_name: typeof u.assigned_admin_name === 'string' ? u.assigned_admin_name : '',
    assigned_to: u.assigned_to != null ? Number(u.assigned_to) : null,
    sgb5_bk: p?.sgb5_bk === true || p?.sgb5_bk === 1,
    // Nur wenn die Zeile aus der BK-Liste stammt (Felder vorhanden), sonst undefined.
    ...('last_absence_end' in u || 'has_open_absence' in u
      ? { availableFrom: availableFromFromRow(u.last_absence_end, u.has_open_absence) }
      : {}),
  };
}

/** Map a PB user (user_role=2) to the UI Client type. */
export function v1UserToClient(u: V1User, profile?: V1Profile | null): Client {
  const p = profile ?? primaryProfile(u);
  // Hauptkontakt ist immer der Pflegeempfänger: ist diese Zeile im
  // Familienkonto selbst nur eine Kontaktperson mit eigenem Account (siehe
  // PbListController::enrichRows — pe_user_id weicht dann von u.id ab), zeigt
  // die Liste/der Drawer-Kopf den Namen des Pflegeempfängers statt der
  // Kontaktperson. Ohne pe_user_id (ältere/andere Quelle) bleibt es wie
  // bisher der eigene Name.
  const peOverride = u.pe_user_id != null && u.pe_user_id !== u.id && !!u.pe_name;
  const displayName = peOverride ? (u.pe_name as string) : fullName(u);
  // Folgeticket-Batch 22.09.2026: bei PE-Override kommen Pflegegrad, Alter
  // (aus pe_geburtsdatum) und Ort ebenfalls vom Pflegeempfänger statt von
  // der Kontaktpersonen-Zeile — sonst zeigt die Liste z. B. den (leeren)
  // Pflegegrad der Kontaktperson statt des tatsächlichen Pflegeempfängers.
  const pg = peOverride ? pflegegradFromRaw(u.pe_pflegegrad) : pflegegradFromRaw(p?.pflegegrad);
  const birth = peOverride ? (u.pe_geburtsdatum ?? '') : (p?.geburtsdatum ?? '');
  const city = peOverride ? (u.pe_ort ?? '') : (p?.ort ?? '');
  return {
    id: `PB-${u.id}`,
    name: displayName,
    birth,
    // 0 = unbekannt (kein Geburtsdatum) — Anzeigen müssen das Alter dann weglassen.
    age: ageFromBirth(birth),
    gender: ANREDE_LABEL[String(p?.anrede ?? '')] ?? '—',
    city,
    plz: p?.plz ?? '',
    address: [p?.strasse, p?.hausnummer].filter(Boolean).join(' '),
    pflegegrad: pg,
    // feature/pflege-phase-summary-filters: BK-Name kommt jetzt direkt am
    // PB-Datensatz (bk_name); fallback "—" wenn (noch) keine Betreuungskraft.
    caregiver: (typeof u.bk_name === 'string' && u.bk_name) ? u.bk_name : '—',
    assigned_to_name: typeof u.assigned_admin_name === 'string' ? u.assigned_admin_name : '',
    status: u.konto === 4 ? 'Aktiv' : u.konto === 2 ? 'Ausstehend' : 'Beendet',
    since: formatDate(u.created_at, ''),
    phone: u.mobile ?? '',
    emergency: '',
    conditions: [],
    allergies: '',
    medications: [],
    notes: '',
    assignments: [],
    documents: [],
    // feature/pb-phase-process
    phase:       typeof u.phase === 'number' ? u.phase : (u.phase ? Number(u.phase) : 1),
    phase_label: typeof u.phase_label === 'string' ? u.phase_label : null,
  };
}

export function v1MessageToEmail(m: V1Message): EmailMessage {
  const created = new Date(m.created_at);
  const isoDate = Number.isNaN(created.getTime())
    ? ''
    : created.toISOString().slice(0, 10);
  const time = Number.isNaN(created.getTime())
    ? ''
    : `${String(created.getHours()).padStart(2, '0')}:${String(created.getMinutes()).padStart(2, '0')}`;
  const subject = m.betreff ?? '(ohne Betreff)';
  return {
    id: m.id,
    from: typeof m.von === 'number' ? `User #${m.von}` : String(m.von ?? 'System'),
    email: '',
    subject,
    preview: (m.text ?? '').slice(0, 140),
    time,
    date: isoDate,
    unread: m.confirmation === 1 && !m.confirmed_at && !m.declined_at,
    starred: false,
    tag: m.confirmation === 1 ? 'Bestätigung' : 'Standard',
    thread: 0,
    folder: 'Inbox',
    attachments: 0,
  };
}
